import { http } from '@/lib/http';
import type {
  BusinessError,
  CreateSupportTicketPayload,
  InboundWebhookEvent,
  ListBusinessErrorsQuery,
  ListInboundWebhooksQuery,
  ListOutboundApiCallsQuery,
  ListOutboundWebhooksQuery,
  ListSupportTicketsQuery,
  OutboundApiCall,
  OutboundWebhookDelivery,
  PlatformSupportTicket,
} from '@/types/observability';

const INBOUND = '/api/v1/utilities/observability/inbound-webhooks';
const OUTBOUND = '/api/v1/utilities/observability/outbound-webhooks';
const OUTBOUND_API = '/api/v1/utilities/observability/outbound-api-calls';
const BUSINESS_ERRORS = '/api/v1/utilities/observability/business-errors';
const TICKETS = '/api/v1/utilities/observability/tickets';

export interface InboundWebhooksPage {
  rows: InboundWebhookEvent[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OutboundWebhooksPage {
  rows: OutboundWebhookDelivery[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OutboundApiCallsPage {
  rows: OutboundApiCall[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BusinessErrorsPage {
  rows: BusinessError[];
  total: number;
  page: number;
  pageSize: number;
}

export const observabilityService = {
  listInboundWebhooks: (q: ListInboundWebhooksQuery = {}) =>
    http.get<InboundWebhooksPage>(INBOUND, { params: q }),

  getInboundWebhook: (id: string) =>
    http.get<InboundWebhookEvent>(`${INBOUND}/${id}`),

  listOutboundWebhooks: (q: ListOutboundWebhooksQuery = {}) =>
    http.get<OutboundWebhooksPage>(OUTBOUND, { params: q }),

  getOutboundWebhook: (id: string) =>
    http.get<OutboundWebhookDelivery>(`${OUTBOUND}/${id}`),

  listOutboundApiCalls: (q: ListOutboundApiCallsQuery = {}) =>
    http.get<OutboundApiCallsPage>(OUTBOUND_API, { params: q }),

  getOutboundApiCall: (id: string) =>
    http.get<OutboundApiCall>(`${OUTBOUND_API}/${id}`),

  listBusinessErrors: (q: ListBusinessErrorsQuery = {}) =>
    http.get<BusinessErrorsPage>(BUSINESS_ERRORS, { params: q }),

  getBusinessError: (id: string) =>
    http.get<BusinessError>(`${BUSINESS_ERRORS}/${id}`),

  // ── Support tickets — "Report issue" CTAs ──────────────────────
  createSupportTicket: (payload: CreateSupportTicketPayload) =>
    http.post<PlatformSupportTicket, CreateSupportTicketPayload>(TICKETS, payload),
  listSupportTickets: (q: ListSupportTicketsQuery = {}) =>
    http.get<SupportTicketsPage>(TICKETS, { params: q }),
  getSupportTicket: (id: string) =>
    http.get<PlatformSupportTicket>(`${TICKETS}/${id}`),
};

export interface SupportTicketsPage {
  rows: PlatformSupportTicket[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Per-partner recent events — same shape as the global log but
 * pre-filtered to one DispatchPartner. Drives the Connection-health
 * card on the wizard's Webhook step.
 */
export const partnerInboundWebhooksService = {
  recent: (partnerId: string, limit = 10) =>
    http.get<InboundWebhookEvent[]>(
      `/api/v1/dispatch/partners/${partnerId}/inbound-webhooks`,
      { params: { limit } },
    ),
};
